import { useEffect, useMemo, useState } from "react";
import {
  Bell,
  CheckCheck,
  ArrowDownToLine,
  ArrowUpFromLine,
  Trophy,
  Gift,
  AlertCircle,
  Info,
  Megaphone,
  Sparkles,
} from "lucide-react";
import { api } from "../../lib/api";
import { ProfileShell } from "../../components/profile/ProfileShell";

// Map notification.type → icon + accent palette. Catches a wide range of
// backend-side notification kinds so admins never see an "unstyled" entry.
const TYPE_META = {
  deposit:        { icon: ArrowDownToLine, color: "#19a55c", glow: "rgba(25,165,92,0.35)",   label: "Deposit" },
  deposit_success:{ icon: ArrowDownToLine, color: "#19a55c", glow: "rgba(25,165,92,0.35)",   label: "Deposit" },
  withdraw:       { icon: ArrowUpFromLine, color: "#3b82f6", glow: "rgba(59,130,246,0.35)",  label: "Withdrawal" },
  withdraw_success:{ icon: ArrowUpFromLine, color: "#3b82f6", glow: "rgba(59,130,246,0.35)", label: "Withdrawal" },
  withdraw_refund:{ icon: ArrowUpFromLine, color: "#f59e0b", glow: "rgba(245,158,11,0.35)",  label: "Refund" },
  win:            { icon: Trophy,          color: "#ffc933", glow: "rgba(255,201,51,0.45)",  label: "Big Win" },
  bonus:          { icon: Gift,            color: "#a855f7", glow: "rgba(168,85,247,0.35)",  label: "Bonus" },
  promotion:      { icon: Sparkles,        color: "#ec4899", glow: "rgba(236,72,153,0.35)",  label: "Promo" },
  announcement:   { icon: Megaphone,       color: "#06b6d4", glow: "rgba(6,182,212,0.35)",   label: "Announcement" },
  alert:          { icon: AlertCircle,     color: "#ef4444", glow: "rgba(239,68,68,0.45)",   label: "Alert" },
  system:         { icon: Info,            color: "#94a3b8", glow: "rgba(148,163,184,0.30)", label: "System" },
};

const metaFor = (type) => TYPE_META[(type || "").toLowerCase()] || TYPE_META.system;

// Friendly relative time: "just now", "5m ago", "2h ago", "3d ago", else date string.
const relTime = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const diff = (Date.now() - t) / 1000;
  if (diff < 30) return "just now";
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

// Group notifications by date bucket (Today / Yesterday / This Week / Earlier)
const groupByDate = (items) => {
  const buckets = { Today: [], Yesterday: [], "This Week": [], Earlier: [] };
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startYesterday = startToday - 86400 * 1000;
  const startWeek = startToday - 6 * 86400 * 1000;
  for (const n of items) {
    const t = new Date(n.created_at).getTime() || 0;
    if (t >= startToday) buckets.Today.push(n);
    else if (t >= startYesterday) buckets.Yesterday.push(n);
    else if (t >= startWeek) buckets["This Week"].push(n);
    else buckets.Earlier.push(n);
  }
  return Object.entries(buckets).filter(([, v]) => v.length > 0);
};

function NotificationCard({ n, onMarkRead }) {
  const m = metaFor(n.type);
  const Icon = m.icon;
  const unread = !n.read;
  return (
    <button
      type="button"
      onClick={() => unread && onMarkRead?.(n.id)}
      data-testid="notification-item"
      className={`w-full text-left rounded-xl p-3 sm:p-4 flex gap-3 transition-all border ${
        unread
          ? "bg-gradient-to-br from-[#101824] to-[#0d1620] border-white/10 hover:border-white/20"
          : "bg-[#0c1219]/60 border-white/[0.04] hover:border-white/10 opacity-80"
      }`}
      style={
        unread
          ? { boxShadow: `inset 3px 0 0 ${m.color}, 0 4px 12px -4px ${m.glow}` }
          : undefined
      }
    >
      <div
        className="h-10 w-10 sm:h-11 sm:w-11 shrink-0 rounded-full flex items-center justify-center relative"
        style={{
          background: `radial-gradient(120% 120% at 30% 30%, ${m.color}33 0%, ${m.color}11 70%)`,
          boxShadow: unread ? `0 0 0 1px ${m.color}55, 0 0 14px ${m.glow}` : "none",
        }}
      >
        <Icon size={18} color={m.color} strokeWidth={2.2} />
        {unread && (
          <span
            aria-hidden
            className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-[#0a1119]"
            style={{ background: m.color, boxShadow: `0 0 8px ${m.glow}` }}
          />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`text-[14px] sm:text-[15px] font-semibold truncate ${unread ? "text-white" : "text-white/75"}`}>
                {n.title}
              </h3>
              <span
                className="hidden sm:inline-flex items-center px-1.5 h-[18px] rounded text-[10px] font-bold uppercase tracking-wider"
                style={{ background: `${m.color}22`, color: m.color, border: `1px solid ${m.color}33` }}
              >
                {m.label}
              </span>
            </div>
            <p className={`text-sm mt-1 leading-relaxed ${unread ? "text-white/75" : "text-white/55"}`}>
              {n.body}
            </p>
          </div>
          {n.read && (
            <CheckCheck size={14} className="text-emerald-500/60 mt-1 shrink-0" aria-label="read" />
          )}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-white/40 text-[11px] sm:text-xs">{relTime(n.created_at)}</span>
          {unread && (
            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: m.color }}>
              ● New
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/profile/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count ?? (data.items || []).filter((x) => !x.read).length);
    } catch {
      setItems([]);
      setUnread(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markAllRead = async () => {
    if (marking || unread === 0) return;
    setMarking(true);
    try {
      await api.post("/profile/notifications/mark-all-read");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch {
      /* silent — user can retry */
    } finally {
      setMarking(false);
    }
  };

  const markOneRead = async (id) => {
    if (!id) return;
    // Optimistic update
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await api.post(`/profile/notifications/${id}/read`);
    } catch {
      /* rollback handled implicitly by next reload */
    }
  };

  const groups = useMemo(() => groupByDate(items), [items]);

  return (
    <ProfileShell title="Notifications">
      {/* Sticky summary header with unread count + Mark all read */}
      <div className="-mx-4 sm:mx-0 px-4 sm:px-0 sticky top-0 z-10 bg-gradient-to-b from-[#0a1119] via-[#0a1119]/95 to-[#0a1119]/0 pt-1 pb-3">
        <div className="flex items-center justify-between gap-3 rounded-xl px-3 sm:px-4 py-2.5 bg-[#101824] border border-white/5">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="relative h-9 w-9 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
              <Bell size={16} className="text-emerald-400" />
              {unread > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[#101824] animate-pulse"
                  data-testid="notif-unread-badge"
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </div>
            <div className="min-w-0">
              <div className="text-white text-sm font-semibold truncate">
                {unread > 0
                  ? `${unread} new ${unread === 1 ? "notification" : "notifications"}`
                  : "All caught up"}
              </div>
              <div className="text-white/45 text-[11px]">
                {items.length > 0 ? `${items.length} total · last 50 shown` : "Latest 50 messages appear here"}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={markAllRead}
            disabled={marking || unread === 0}
            data-testid="mark-all-read-btn"
            className={`shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold transition-all ${
              unread === 0
                ? "bg-white/[0.04] text-white/30 cursor-not-allowed"
                : "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 active:scale-[0.97]"
            }`}
          >
            <CheckCheck size={13} />
            Mark all read
          </button>
        </div>
      </div>

      <div className="pb-10 space-y-5" data-testid="notifications-list">
        {loading && (
          <div className="space-y-3 pt-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-xl bg-[#101824] border border-white/5 h-[78px] animate-pulse" />
            ))}
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-center py-16 rounded-xl bg-[#0c1219]/60 border border-white/5">
            <div className="inline-flex h-16 w-16 rounded-full items-center justify-center bg-white/[0.04] mb-3">
              <Bell size={28} className="text-white/30" strokeWidth={1.5} />
            </div>
            <p className="text-white/70 text-sm font-semibold">You're all caught up</p>
            <p className="mt-1 text-white/40 text-xs">New deposits, wins and offers will appear here.</p>
          </div>
        )}

        {!loading &&
          groups.map(([label, group]) => (
            <section key={label}>
              <h4 className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/35 mb-2 px-1">
                {label}
              </h4>
              <div className="space-y-2.5">
                {group.map((n) => (
                  <NotificationCard
                    key={n.id || n.created_at}
                    n={n}
                    onMarkRead={markOneRead}
                  />
                ))}
              </div>
            </section>
          ))}
      </div>
    </ProfileShell>
  );
}
